import React from "react";
import {
  siteDarkBlue,
  siteLightBlue,
  sitePurple,
  siteDarkGreen,
  siteYellow
} from "./providers/Theme";
import Home from "./pages/Home";
import FoodAndAllergyBasics from "./pages/FoodAndAllergyBasics";
import Trainings from "./pages/Trainings";
import KyahsEpiCourseForSchools from "./pages/KyahsEpiCourseForSchools";
import KyahsEpiCourseForFoodService from "./pages/KyahsEpiCourseForFoodService";
import KyahsEpiCourseForArizonaSchools from "./pages/KyahsEpiCourseForArizonaSchools";
import GetInvolved from "./pages/GetInvolved";
import LawsPolicyLegislation from "./pages/LawsPolicyLegislation";
import AdvocateForChange from "./pages/AdvocateForChange";
import InternshipsAndVolunteers from "./pages/InternshipsAndVolunteers";
import Amendments from "./pages/Amendments";
import AboutUs from "./pages/AboutUs";
import FoodAllergy101 from "./pages/FoodAllergy101";
import TriggerFoods from "./pages/TriggerFoods";
import FoodAllergyRisks from "./pages/FoodAllergyRisks";
import Anaphylaxis from "./pages/Anaphylaxis";
import Epinephrine from "./pages/Epinephrine";
import EpinephrineFAQ from "./pages/EpinephrineFAQ";
import EpinephrineTypes from "./pages/EpinephrineTypes";
import HowToGetEpinephrine from "./pages/HowToGetEpinephrine";
import FactsAndFigures from "./pages/FactsAndFigures";
import Collaborators from "./pages/Collaborators";
import KyahsStory from "./pages/KyahsStory";
import OurTeam from "./pages/OurTeam";
import BoardOfDirectors from "./pages/BoardOfDirectors";
import StrategicTeamPage from "./pages/StrategicTeamPage";
import MedicalAdvisoryCouncil from "./pages/MedicalAdvisoryCouncil";
import InTheNews from "./pages/InTheNews";
import NewsletterArchive from "./pages/NewsletterArchive";
import Donate from "./pages/Donate";
import Thankyou from "./pages/Thankyou";
import { StyledSpan } from "./pages/shared.styled.components";

interface Route {
  title: string;
  path: string;
  key: string;
  component: React.FunctionComponent<any>;
  navDropDownComponent?: React.ReactElement;
}

interface NavConfig {
  displayAs: string;
  color: string;
}

interface DropdownNavConfig extends NavConfig {
  displayAs: "Dropdown";
  children: Array<string | { key: string; children: string[] }>;
}

interface LinkNavConfig extends NavConfig {
  displayAs: "Link";
}

type NavItemConfig = DropdownNavConfig | LinkNavConfig;

interface PageItem extends Route {
  showOnNavBar?: false;
  navConfig?: undefined;
}

export interface NavItem extends Route {
  showOnNavBar: true;
  navConfig: NavItemConfig;
}

export type Page = PageItem | NavItem;

type PagesConfigObj = Record<string, Page>;

export const HomePage: PageItem = {
  title: "Home",
  path: "/",
  key: "home",
  component: Home
};

const PagesConfig: PagesConfigObj = {
  home: HomePage,
  "food-allergy-basics": {
    title: "Food Allergy Basics",
    path: "/food-allergy-basics",
    key: "food-allergy-basics",
    component: FoodAndAllergyBasics,
    showOnNavBar: true,
    navConfig: {
      displayAs: "Dropdown",
      color: siteDarkBlue,
      children: [
        "food-allergy-101",
        "facts-and-figures",
        "trigger-foods",
        "food-allergy-risks",
        "anaphylaxis",
        {
          key: "epinephrine",
          children: [
            "types-of-epinephrine-devices",
            "how-to-get-epinephrine",
            "epinephrine-faq"
          ]
        }
      ]
    }
  },
  "food-allergy-101": {
    title: "Food Allergy 101",
    path: "/food-allergy-basics/food-allergy-101",
    key: "food-allergy-101",
    component: FoodAllergy101
  },
  "trigger-foods": {
    title: "Trigger Foods",
    path: "/food-allergy-basics/trigger-foods",
    key: "trigger-foods",
    component: TriggerFoods
  },
  "food-allergy-risks": {
    title: "Food Allergy Risks",
    path: "/food-allergy-basics/food-allergy-risks",
    key: "food-allergy-risks",
    component: FoodAllergyRisks
  },
  anaphylaxis: {
    title: "Anaphylaxis",
    path: "/food-allergy-basics/anaphylaxis",
    key: "anaphylaxis",
    component: Anaphylaxis
  },
  epinephrine: {
    title: "Epinephrine",
    path: "/food-allergy-basics/epinephrine",
    key: "epinephrine",
    component: Epinephrine
  },
  "epinephrine-faq": {
    title: "Epinephrine FAQ",
    path: "/food-allergy-basics/epinephrine/epinephrine-faq",
    key: "epinephrine-faq",
    component: EpinephrineFAQ
  },
  "types-of-epinephrine-devices": {
    title: "Types of Epinephrine Devices",
    path: "/food-allergy-basics/epinephrine/types-of-epinephrine-devices",
    key: "types-of-epinephrine-devices",
    component: EpinephrineTypes
  },
  "how-to-get-epinephrine": {
    title: "How to Get Epinephrine",
    path: "/food-allergy-basics/epinephrine/how-to-get-epinephrine",
    key: "how-to-get-epinephrine",
    component: HowToGetEpinephrine
  },
  "facts-and-figures": {
    title: "Facts and Figures",
    path: "/food-allergy-basics/facts-and-figures",
    key: "facts-and-figures",
    component: FactsAndFigures
  },
  trainings: {
    title: "Trainings",
    path: "/trainings",
    key: "trainings",
    component: Trainings,
    showOnNavBar: true,
    navConfig: {
      displayAs: "Dropdown",
      color: siteLightBlue,
      children: [
        "kyahs-epicourse-for-arizona-schools",
        "kyahs-epicourse-for-schools",
        "kyahs-epicourse-for-food-service"
      ]
    }
  },
  "kyahs-epicourse-for-schools": {
    title: "National Kyah's EPICourse for Schools",
    path: "/trainings/kyahs-epicourse-for-schools",
    key: "kyahs-epicourse-for-schools",
    component: KyahsEpiCourseForSchools
  },
  "kyahs-epicourse-for-arizona-schools": {
    title: "Kyah's EPICourse for Arizona Schools",
    navDropDownComponent: (
      <>
        Kyah's EPICourse for <StyledSpan color="red">Arizona</StyledSpan>{" "}
        Schools
      </>
    ),
    path: "/trainings/kyahs-epicourse-for-arizona-schools",
    key: "kyahs-epicourse-for-arizona-schools",
    component: KyahsEpiCourseForArizonaSchools
  },
  "kyahs-epicourse-for-food-service": {
    title: "Kyah's EPICourse for Food Service",
    path: "/trainings/kyahs-epicourse-for-food-service",
    key: "kyahs-epicourse-for-food-service",
    component: KyahsEpiCourseForFoodService
  },
  "get-involved": {
    title: "Get Involved",
    path: "/get-involved",
    key: "get-involved",
    component: GetInvolved,
    showOnNavBar: true,
    navConfig: {
      displayAs: "Dropdown",
      color: sitePurple,
      children: [
        "laws-policy-legislation",
        "advocate-for-change",
        "internships-and-volunteers"
      ]
    }
  },
  "laws-policy-legislation": {
    title: "Laws, Policy & Legislation",
    path: "/get-involved/laws-policy-legislation",
    key: "laws-policy-legislation",
    component: LawsPolicyLegislation
  },
  "advocate-for-change": {
    title: "Advocate for Change",
    path: "/get-involved/advocate-for-change",
    key: "advocate-for-change",
    component: AdvocateForChange
  },
  "internships-and-volunteers": {
    title: "Internships and Volunteers",
    path: "/get-involved/internships-and-volunteers",
    key: "internships-and-volunteers",
    component: InternshipsAndVolunteers
  },
  "about-us": {
    title: "About Us",
    path: "/about-us",
    key: "about-us",
    component: AboutUs,
    showOnNavBar: true,
    navConfig: {
      displayAs: "Dropdown",
      color: siteDarkGreen,
      children: [
        "kyahs-story",
        {
          key: "our-team",
          children: [
            "board-of-directors",
            "strategic-team",
            "medical-advisory-council"
          ]
        },
        "in-the-news",
        "newsletter-archive",
        "collaborators"
      ]
    }
  },
  collaborators: {
    title: "Collaborators",
    path: "/about-us/collaborators",
    key: "collaborators",
    component: Collaborators
  },
  "our-team": {
    title: "Our Team",
    path: "/about-us/our-team",
    key: "our-team",
    component: OurTeam
  },
  "board-of-directors": {
    title: "Board of Directors",
    path: "/about-us/our-team/board-of-directors",
    key: "board-of-directors",
    component: BoardOfDirectors
  },
  "strategic-team": {
    title: "Strategic Team",
    path: "/about-us/our-team/strategic-team",
    key: "strategic-team",
    component: StrategicTeamPage
  },
  "medical-advisory-council": {
    title: "Medical Advisory Council",
    path: "/about-us/our-team/medical-advisory-council",
    key: "medical-advisory-council",
    component: MedicalAdvisoryCouncil
  },
  "kyahs-story": {
    title: "Kyah's Story",
    path: "/about-us/kyahs-story",
    key: "kyahs-story",
    component: KyahsStory
  },
  "in-the-news": {
    title: "In the News",
    path: "/about-us/in-the-news",
    key: "in-the-news",
    component: InTheNews
  },
  "newsletter-archive": {
    title: "Newsletter Archive",
    path: "/about-us/newsletter-archive",
    key: "newsletter-archive",
    component: NewsletterArchive
  },
  amendments: {
    title: "Amendments",
    path: "/about-us/in-the-news/amendments",
    key: "amendments",
    component: Amendments
  },
  thankyou: {
    title: "Thank you",
    path: "/thankyou",
    key: "thankyou",
    component: Thankyou
  },
  donate: {
    title: "Donate",
    path: "/donate",
    key: "donate",
    component: Donate,
    showOnNavBar: true,
    navConfig: {
      displayAs: "Link",
      color: siteYellow
    }
  }
};

export default PagesConfig;
